import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { getURI } from '../../api/apiConfig';

// @mui/material components
import Icon from '@mui/material/Icon';
import InputAdornment from '@mui/material/InputAdornment';

import { toast } from 'react-toastify';
import { makeStyles } from '@mui/styles';

// core components
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import CardFooter from 'components/Card/CardFooter.js';
import CardHeader from 'components/Card/CardHeader.js';
import Button from 'components/CustomButtons/Button.js';
import CustomInput from 'components/CustomInput/CustomInput.js';
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';

import { generateCorrelationId } from '@src/helpers/generateCorrelationId';
import { useGetUserEmailAddressQuery } from 'api/user.api';
import styles from 'assets/jss/material-dashboard-pro-react/views/loginPageStyle.js';

const useStyles = makeStyles(styles);

export default function Register(props) {
  const [cardAnimaton, setCardAnimation] = useState('cardHidden');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [email, setEmail] = useState('');
  const [emailState, setEmailState] = useState('');
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [passwordState, setPasswordState] = useState('');
  const [confirmPasswordState, setConfirmPasswordState] = useState('');
  const { userId } = useParams();
  const location = useLocation();
  const { data: userEmailAddress } = useGetUserEmailAddressQuery(userId);

  useEffect(() => {
    let id = setTimeout(function () {
      setCardAnimation('');
    }, 700);
    // Specify how to clean up after this effect:
    return function cleanup() {
      window.clearTimeout(id);
    };
  });

  useEffect(() => {
    if (userEmailAddress !== null) {
      setEmailAddress(userEmailAddress);
    }
  }, [userEmailAddress]);

  const classes = useStyles();

  const setEmailAddress = (emailAddress) => {
    setEmail(emailAddress);
    const params = new URLSearchParams(location.search);
    setToken(params.get('token'));
    setEmailState('success');
  };

  const validatePassword = (password) => {
    if (
      password.length > 7 &&
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[ !"#$%&'()*+,-./:;<=>?@[\]^_`{|}~])/.test(
        password
      )
    ) {
      setPassword(password);
      setPasswordState('success');
      setPasswordState('success');
    } else {
      setPasswordState('error');
    }
  };

  const confirmPassword = (secondPassword) => {
    if (password !== secondPassword) {
      setConfirmPasswordState('error');
    } else {
      setConfirmPasswordState('success');
    }
  };

  const getErrorMessage = (response, data) => {
    if (data.message) {
      return data.message;
    }

    if (response.status === 400) {
      return 'Please fill all the fields correctly!';
    }

    if (response.status === 401) {
      return 'Invalid email and password combination.';
    }

    return 'Something went wrong! Please try again later.';
  };

  const formSubmitHandler = async (e) => {
    e.preventDefault();
    if (emailState === 'success' && passwordState === 'success') {
      setIsSubmitting(true);
      const headers = new Headers();
      headers.append('Content-Type', 'application/json');
      headers.append('X-CORRELATION-ID', generateCorrelationId());

      try {
        const response = await fetch(getURI('register_user'), {
          method: 'PUT',
          headers,
          credentials: 'include',
          body: JSON.stringify({
            token: token,
            email_address: email,
            password: password,
          }),
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          const errorMessage = getErrorMessage(response, data);
          toast.error(errorMessage, { position: toast.POSITION.BOTTOM_CENTER });
        } else {
          props.history.replace('/auth/confirm_email');
        }
      } catch (error) {
        toast.error('Something went wrong! Please try again later.', {
          position: toast.POSITION.BOTTOM_CENTER,
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className={classes.container}>
      <GridContainer justifyContent='center'>
        <GridItem xs={12} sm={6} md={4}>
          <Card login className={classes[cardAnimaton]}>
            <CardHeader
              className={`${classes.cardHeader} ${classes.textCenter}`}
              color='info'
            >
              <h4 className={classes.cardTitle}>Set Password</h4>
            </CardHeader>
            <CardBody>
              <CustomInput
                success={passwordState === 'success'}
                error={passwordState === 'error'}
                labelText='Password'
                id='password'
                value={password}
                formControlProps={{
                  fullWidth: true,
                }}
                inputProps={{
                  onChange: (e) => validatePassword(e.target.value),
                  endAdornment: (
                    <InputAdornment position='end'>
                      <Icon className={classes.inputAdornmentIcon}>
                        lock_outline
                      </Icon>
                    </InputAdornment>
                  ),
                  type: 'password',
                  autoComplete: 'off',
                }}
                helperText={
                  passwordState === 'error'
                    ? 'The password must be alphanumeric with special character and be more than 8 characters long'
                    : undefined
                }
              />
              <CustomInput
                success={confirmPasswordState === 'success'}
                error={confirmPasswordState === 'error'}
                labelText='Re-enter Password'
                id='confirmPassword'
                value={password}
                formControlProps={{
                  fullWidth: true,
                }}
                inputProps={{
                  onChange: (e) => confirmPassword(e.target.value),
                  endAdornment: (
                    <InputAdornment position='end'>
                      <Icon className={classes.inputAdornmentIcon}>
                        lock_outline
                      </Icon>
                    </InputAdornment>
                  ),
                  type: 'password',
                  autoComplete: 'off',
                }}
                helperText={
                  confirmPasswordState === 'error'
                    ? 'Passwords do not match!'
                    : undefined
                }
              />
            </CardBody>
            <CardFooter className={classes.justifyContentCenter}>
              <Button
                color='info'
                size='lg'
                block
                disabled={
                  isSubmitting ||
                  emailState !== 'success' ||
                  passwordState !== 'success' ||
                  confirmPasswordState !== 'success'
                }
                onClick={formSubmitHandler}
                text={`$`}
              >
                {isSubmitting ? 'Registering' : 'Register'}
              </Button>
            </CardFooter>
          </Card>
        </GridItem>
      </GridContainer>
    </div>
  );
}
